/**
 * What: Draws a Worker-sent `text` block (one line or a multi-line box) and reports what the merchant types.
 * Transport / data-flow: Every change is reported at the `typing` pace (the hook keeps one waiting edit per field and checks
 *   after a pause). `value` is already the newest waiting value for this field, else the Worker's: the renderer lays
 *   waiting edits over the payload, so this block holds no typed-text state and no rule about when to trust the Worker.
 */
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import type { OnEdit } from './SectionBlocks'

type Block = Extract<BuilderBlock, { kind: 'text' }>

export function TextBlock({
  block,
  onEdit,
  readOnly,
  value,
  error,
  labelHidden = false,
  required = false,
}: {
  block: Block
  onEdit: OnEdit
  readOnly: boolean
  value: string
  /** The Worker's message for this field, already withheld while the field has an unanswered edit (`visibleMessage`). */
  error: string | undefined
  /** The label is drawn elsewhere (a group badge); keep it for assistive tech but hide it. */
  labelHidden?: boolean
  /** The field must be filled in; the caller draws the "*", this marks it for assistive tech. */
  required?: boolean
}) {
  const props = {
    label: block.label,
    required,
    labelAccessibilityVisibility: labelHidden ? ('exclusive' as const) : undefined,
    error,
    value,
    placeholder: block.placeholder ?? undefined,
    disabled: block.disabled || readOnly,
    maxLength: block.maxLength,
    autocomplete: 'off',
  } as const
  const prefix = block.prefix ?? undefined
  const onInput = (event: { currentTarget: { value: string } }) =>
    onEdit(block.key, event.currentTarget.value, 'typing')
  return block.rows === null ? (
    <s-text-field {...props} prefix={prefix} onInput={onInput} />
  ) : (
    <s-text-area {...props} rows={block.rows} onInput={onInput} />
  )
}
